import React, { useState } from "react";
import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_ORDER_ROUTER } from "../../../services/api-config";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import { formatKg } from "../../../utils/helpers";

interface Props {
  orderId: number;
  orderCode?: string;
  /** Sản lượng hiện tại (kg). */
  currentWeight: number;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Sửa sản lượng (cân lần 2) của đơn ĐÃ HOÀN TẤT (`POST :id/completed-final-weight`). Chỉ đổi sản lượng, không đổi
 * tiền; kg các khâu đổi theo tỷ lệ và tiền công tính lại. Đơn tính tiền theo kg phải sửa qua "Chỉnh SL / đơn giá"
 * (BE chặn và trả thông báo).
 */
const EditFinalWeightModal: React.FC<Props> = ({ orderId, orderCode, currentWeight, onClose, onSaved }) => {
  const { request: post } = useFetchData.Post<any>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  const [weight, setWeight] = useState(String(Number(currentWeight) || ""));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const value = Number(weight);
  const valid = Number.isFinite(value) && value > 0 && /^\d+(\.\d{1,2})?$/.test(weight.trim());
  const unchanged = valid && Math.round(value * 100) === Math.round((Number(currentWeight) || 0) * 100);

  const save = async () => {
    if (!valid || unchanged || !reason.trim()) return;
    setSaving(true);
    try {
      const res = await post({
        endPoint: API_ORDER_ROUTER.API_COMPLETED_FINAL_WEIGHT(orderId),
        data: { weight: value, reason: reason.trim() },
      });
      if (!res || !isSuccess(res)) {
        showToast({ message: res?.message || "Sửa sản lượng thất bại", type: "error" });
        return;
      }
      showToast({ message: `Đã sửa sản lượng thành ${formatKg(value)} kg`, type: "success" });
      onSaved();
    } catch (e: any) {
      showToast({ message: e?.message || "Sửa sản lượng thất bại", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[480px] max-w-[95vw] flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-slate-800">Sửa sản lượng sau giặt</h3>
          <p className="text-xs text-slate-500 mt-1">
            Đơn {orderCode ? <b>{orderCode}</b> : `#${orderId}`} — hiện tại: <b>{formatKg(currentWeight)} kg</b>
          </p>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Sản lượng mới (kg) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              inputMode="decimal"
              min={0.01}
              step={0.01}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-300 focus:outline-none"
            />
            {weight.trim() !== "" && !valid && (
              <p className="mt-1 text-xs text-red-600">Sản lượng phải lớn hơn 0, tối đa 2 số lẻ.</p>
            )}
          </div>

          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            Chỉ đổi sản lượng, không đổi tiền đơn. Kg của các khâu (nhận, giặt, giao, kiểm đếm…) đổi theo cùng tỷ lệ,
            tiền công tính lại. Báo cáo sản lượng của kỳ đơn hoàn thành sẽ thay đổi theo.
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Lý do <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-300 focus:outline-none resize-none"
              rows={2}
              maxLength={500}
              placeholder="VD: Cân nhầm, cân lại được 30 kg..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border rounded-lg text-slate-700 hover:bg-slate-100 transition"
          >
            Trở lại
          </button>
          <button
            onClick={save}
            disabled={saving || !valid || unchanged || !reason.trim()}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition disabled:opacity-50"
          >
            {saving ? "Đang lưu..." : "Lưu sản lượng"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditFinalWeightModal;
